import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface BusinessForGraceDialog {
  id: string;
  name: string;
  subscription_status: string;
}

interface SuperAdminExtendGraceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  business: BusinessForGraceDialog | null;
  defaultDays?: number;
  onSuccess: () => void;
}

export function SuperAdminExtendGraceDialog({
  open,
  onOpenChange,
  business,
  defaultDays = 7,
  onSuccess,
}: SuperAdminExtendGraceDialogProps) {
  const { toast } = useToast();
  const [days, setDays] = useState<string>(String(defaultDays));
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (open) setDays(String(defaultDays));
  }, [open, defaultDays]);

  const daysInt = useMemo(() => {
    const n = Number.parseInt(String(days || "").trim(), 10);
    return Number.isFinite(n) ? n : NaN;
  }, [days]);

  const statusLabel = useMemo(() => {
    const s = String(business?.subscription_status || "");
    if (s === "trial") return "trial";
    if (s === "grace_period") return "subscription grace period";
    return s ? s : "subscription";
  }, [business?.subscription_status]);

  const helpText = useMemo(() => {
    if (!business) return "";
    if (business.subscription_status === "trial") {
      return "This extends the business trial end date (and therefore also extends the trial grace lock window).";
    }
    if (business.subscription_status === "grace_period") {
      return "This extends the subscription grace period end date.";
    }
    return "This business is not currently in trial or grace period. Extending may not have any effect.";
  }, [business]);

  const handleSubmit = async () => {
    if (!business) return;
    if (!Number.isFinite(daysInt) || daysInt <= 0) {
      toast({
        title: "Enter a valid number of days",
        description: "Days must be a positive whole number.",
        variant: "destructive",
      });
      return;
    }

    setProcessing(true);
    try {
      const { data, error } = await (supabase as any).rpc(
        "super_admin_extend_business_grace_period",
        {
          _business_id: business.id,
          _days: daysInt,
        }
      );
      if (error) throw error;

      const row = Array.isArray(data) ? data[0] : data;
      toast({
        title: "Grace extended",
        description: `${business.name} was extended by ${daysInt} day${daysInt === 1 ? "" : "s"} (${statusLabel}).`,
      });

      // Best effort: keep UI fresh
      if (row) void row;
      onSuccess();
      onOpenChange(false);
    } catch (e: any) {
      toast({
        title: "Failed to extend grace",
        description: e?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  if (!business) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Extend grace period</DialogTitle>
          <DialogDescription>
            Add days to this business’s {statusLabel}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-2">
            <Label htmlFor="extend-grace-days">Days</Label>
            <Input
              id="extend-grace-days"
              inputMode="numeric"
              value={days}
              onChange={(e) => setDays(e.target.value)}
              placeholder="7"
              disabled={processing}
            />
            {helpText ? (
              <p className="text-xs text-muted-foreground">{helpText}</p>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setDays("7")}
              disabled={processing}
            >
              +7 days
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setDays("14")}
              disabled={processing}
            >
              +14 days
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setDays("30")}
              disabled={processing}
            >
              +30 days
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={processing}
          >
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={processing}>
            {processing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Extend
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

